import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';
import util from 'preact-util';

import { createGame, step, WIDTH, HEIGHT } from '../../lib/arcade/pacmanEngine';
import { render as renderFrame } from '../../lib/arcade/pacmanRenderer';
import { createPacmanSound } from '../../lib/arcade/pacmanSound';
import { createGameLoop } from '../../lib/arcade/gameLoop';
import ArcadeLeaderboard from './leaderboard';

/**
 * Pac-Man drawer UI (C). Owns one engine state, the shared fixed-step loop,
 * keyboard/swipe/d-pad input, HUD + screens, sounds and score submission.
 */
export const GAME_KEY = 'pacman';

const HUD_RESERVED_PX = 170;
const TOUCH_CONTROLS_PX = 130;
const SWIPE_MIN_PX = 16;
const KEY_DIRS = {
    ArrowUp: 'up', KeyW: 'up', ArrowDown: 'down', KeyS: 'down', ArrowLeft: 'left', KeyA: 'left', ArrowRight: 'right', KeyD: 'right',
};
const KEY_PAUSE = ['KeyP'];
const KEY_START = ['Space', 'Enter'];

function isTouchDevice() {
    if (typeof window === 'undefined') {
        return false;
    }
    return 'ontouchstart' in window || (window.innerWidth || 0) < 768;
}

@observer
class Pacman extends Component {
    constructor(props) {
        super(props);
        const muted = !!util.get('arcade.muted');
        this.state = {
            screen: 'title',
            engineStatus: 'ready',
            score: 0,
            level: 1,
            lives: 3,
            scale: 1,
            isTouch: isTouchDevice(),
            submitting: false,
            muted,
        };
        this.sound = createPacmanSound({ muted });
        this.loop = createGameLoop({ onTick: this.tick, onFrame: this.afterFrame });
        this.game = null;
        this.pendingDir = null;
        this.frame = 0;
        this.touchStart = null;
        this.swipeDone = false;
        this.startedAt = 0;
        this.unmounted = false;
        this.canvasRef = null;
        this.wrapRef = null;
    }

    componentDidMount() {
        window.addEventListener('keydown', this.onKeyDown);
        window.addEventListener('resize', this.fitCanvas);
        window.addEventListener('blur', this.pause);
        document.addEventListener('visibilitychange', this.onVisibility);
        this.fitCanvas();
        this.drawIdle();
        this.loadScores();
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevState.scale !== this.state.scale) {
            const ctx = this.getCtx();
            if (ctx && this.game) {
                renderFrame(ctx, this.game, this.frame);
            } else {
                this.drawIdle();
            }
        }
    }

    componentWillUnmount() {
        this.unmounted = true;
        this.loop.stop();
        this.sound.dispose();
        window.removeEventListener('keydown', this.onKeyDown);
        window.removeEventListener('resize', this.fitCanvas);
        window.removeEventListener('blur', this.pause);
        document.removeEventListener('visibilitychange', this.onVisibility);
    }

    loadScores = async () => {
        const { arcadeScoreStore } = this.props.stores;
        if (arcadeScoreStore?.loadLeaderboard) {
            await arcadeScoreStore.loadLeaderboard(GAME_KEY);
        }
    }

    getCtx() {
        return this.canvasRef ? this.canvasRef.getContext('2d') : null;
    }

    fitCanvas = () => {
        const wrapWidth = this.wrapRef ? this.wrapRef.clientWidth : WIDTH;
        const reserved = HUD_RESERVED_PX + (this.state.isTouch ? TOUCH_CONTROLS_PX : 0);
        const availableHeight = Math.max(HEIGHT, (window.innerHeight || HEIGHT) - reserved);
        const raw = Math.min(wrapWidth / WIDTH, availableHeight / HEIGHT);
        const scale = Math.max(1, Math.floor(raw * 2) / 2);
        if (scale !== this.state.scale) {
            this.setState({ scale });
        }
    }

    drawIdle() {
        const ctx = this.getCtx();
        if (ctx) {
            renderFrame(ctx, createGame(), 0);
        }
    }

    startGame = () => {
        this.sound.unlock();
        this.game = createGame();
        this.startedAt = Date.now();
        this.pendingDir = null;
        this.frame = 0;
        this.setState({
            screen: 'game', engineStatus: 'ready', score: 0, level: 1, lives: 3, submitting: false,
        }, () => {
            if (!this.unmounted) {
                this.loop.start();
            }
        });
    }

    pause = () => {
        if (this.state.screen !== 'game') {
            return;
        }
        this.loop.stop();
        this.setState({ screen: 'paused' });
    }

    resume = () => {
        if (this.state.screen !== 'paused') {
            return;
        }
        this.sound.unlock();
        this.setState({ screen: 'game' }, () => {
            if (!this.unmounted) {
                this.loop.start();
            }
        });
    }

    togglePause = () => {
        if (this.state.screen === 'game') {
            this.pause();
        } else if (this.state.screen === 'paused') {
            this.resume();
        }
    }

    onVisibility = () => {
        if (document.hidden) {
            this.pause();
        }
    }

    close = () => {
        const { appState } = this.props.stores;
        const { drawerLevel = 1 } = this.props;
        appState.toggleDrawer(false, drawerLevel);
    }

    toggleMute = (e) => {
        if (e && e.currentTarget && typeof e.currentTarget.blur === 'function') {
            e.currentTarget.blur();
        }
        const muted = !this.state.muted;
        this.sound.setMuted(muted);
        util.set('arcade.muted', muted);
        this.setState({ muted });
    }

    tick = (dtMs) => {
        if (!this.game) {
            return;
        }
        step(this.game, { dir: this.pendingDir }, dtMs);
        this.pendingDir = null;
    }

    afterFrame = () => {
        if (!this.game) {
            return;
        }
        this.frame += 1;
        const events = this.game.events.splice(0);
        for (let i = 0; i < events.length; i += 1) {
            this.sound.play(events[i]);
        }
        const ctx = this.getCtx();
        if (ctx) {
            renderFrame(ctx, this.game, this.frame);
        }
        this.syncHud();
        if (this.game.status === 'gameOver') {
            this.loop.stop();
            this.onGameOver();
        }
    }

    syncHud() {
        const {
            score, level, lives, status,
        } = this.game;
        if (score !== this.state.score || level !== this.state.level || lives !== this.state.lives || status !== this.state.engineStatus) {
            this.setState({
                score, level, lives, engineStatus: status,
            });
        }
    }

    onGameOver = async () => {
        const { score, level } = this.game;
        const durationMs = Date.now() - this.startedAt;
        this.setState({ screen: 'gameOver', submitting: score > 0 });
        const { arcadeScoreStore } = this.props.stores;
        if (score > 0 && arcadeScoreStore?.submitScore) {
            await arcadeScoreStore.submitScore({
                game: GAME_KEY, score, wave: level, durationMs,
            });
            await arcadeScoreStore.loadLeaderboard(GAME_KEY);
        }
        if (!this.unmounted) {
            this.setState({ submitting: false });
        }
    }

    onKeyDown = (e) => {
        const { screen } = this.state;
        const { code } = e;
        if (screen === 'game' || screen === 'paused') {
            if (KEY_DIRS[code]) {
                this.pendingDir = KEY_DIRS[code];
                e.preventDefault();
            } else if (KEY_PAUSE.includes(code) && !e.repeat) {
                this.togglePause();
                e.preventDefault();
            }
            return;
        }
        if ((screen === 'title' || screen === 'gameOver') && !e.repeat && KEY_START.includes(code)) {
            e.preventDefault();
            this.startGame();
        }
    }

    touchPoint(e) {
        const touch = (e.touches && e.touches[0]) || (e.changedTouches && e.changedTouches[0]);
        return touch ? { x: touch.clientX, y: touch.clientY } : null;
    }

    onCanvasTouchStart = (e) => {
        if (this.state.screen !== 'game') {
            return;
        }
        e.preventDefault();
        this.touchStart = this.touchPoint(e);
        this.swipeDone = false;
    }

    onCanvasTouchMove = (e) => {
        if (this.state.screen !== 'game' || !this.touchStart || this.swipeDone) {
            return;
        }
        e.preventDefault();
        const point = this.touchPoint(e);
        if (!point) {
            return;
        }
        const dx = point.x - this.touchStart.x;
        const dy = point.y - this.touchStart.y;
        if (Math.max(Math.abs(dx), Math.abs(dy)) < SWIPE_MIN_PX) {
            return;
        }
        if (Math.abs(dx) > Math.abs(dy)) {
            this.pendingDir = dx > 0 ? 'right' : 'left';
        } else {
            this.pendingDir = dy > 0 ? 'down' : 'up';
        }
        this.swipeDone = true;
    }

    onCanvasTouchEnd = (e) => {
        if (this.state.screen !== 'game') {
            return;
        }
        e.preventDefault();
        this.touchStart = null;
    }

    dpad = dir => (e) => {
        if (e && e.cancelable) {
            e.preventDefault();
        }
        this.pendingDir = dir;
    }

    renderDpad() {
        return (
            <div class='d-flex flex-column align-items-center gap-1 mt-3'>
                <button
                    type='button'
                    class='btn btn-outline-light arcade-touch-btn'
                    onTouchStart={this.dpad('up')}
                    onMouseDown={this.dpad('up')}
                >
                    <i class='fa-solid fa-caret-up' />
                </button>
                <div class='d-flex gap-4'>
                    <button
                        type='button'
                        class='btn btn-outline-light arcade-touch-btn'
                        onTouchStart={this.dpad('left')}
                        onMouseDown={this.dpad('left')}
                    >
                        <i class='fa-solid fa-caret-left' />
                    </button>
                    <button
                        type='button'
                        class='btn btn-outline-light arcade-touch-btn'
                        onTouchStart={this.dpad('right')}
                        onMouseDown={this.dpad('right')}
                    >
                        <i class='fa-solid fa-caret-right' />
                    </button>
                </div>
                <button
                    type='button'
                    class='btn btn-outline-light arcade-touch-btn'
                    onTouchStart={this.dpad('down')}
                    onMouseDown={this.dpad('down')}
                >
                    <i class='fa-solid fa-caret-down' />
                </button>
            </div>
        );
    }

    render() {
        const {
            screen, engineStatus, score, level, lives, scale, isTouch, submitting, muted,
        } = this.state;
        const { arcadeScoreStore } = this.props.stores;
        const me = arcadeScoreStore?.leaderboardGame === GAME_KEY ? arcadeScoreStore.me : arcadeScoreStore?.bestByGame?.[GAME_KEY];
        const lastSubmit = arcadeScoreStore?.lastSubmit || null;
        const best = Math.max(me?.score || 0, lastSubmit?.best?.score || 0, score);
        const cssW = `${WIDTH * scale}px`;
        const cssH = `${HEIGHT * scale}px`;
        const inGame = screen === 'game' || screen === 'paused';

        return (
            <div class='d-flex flex-column align-items-center p-3 pb-5 bg-black text-white min-vh-100' ref={c => { this.wrapRef = c; }}>
                <div class='arcade-hud d-flex justify-content-between w-100 mb-2 small' style={`max-width: ${cssW};`}>
                    <span><Text id='arcade.score'>Score</Text> {score}</span>
                    <span><Text id='arcade.level'>Level</Text> {level}</span>
                    <span><Text id='arcade.lives'>Lives</Text> {'▲'.repeat(Math.max(0, lives))}</span>
                    <span><Text id='arcade.best'>Best</Text> {best}</span>
                    <button type='button' class='btn btn-sm btn-outline-light py-0 px-2 lh-1' onClick={this.toggleMute}>
                        <i class={`fa-solid ${muted ? 'fa-volume-xmark' : 'fa-volume-high'}`} />
                        <span class='visually-hidden'>
                            {muted ? <Text id='arcade.unmute'>Unmute</Text> : <Text id='arcade.mute'>Mute</Text>}
                        </span>
                    </button>
                </div>

                <div class='arcade-screen' style={`width: ${cssW}; height: ${cssH};`}>
                    <canvas
                        ref={c => { this.canvasRef = c; }}
                        width={WIDTH}
                        height={HEIGHT}
                        class='arcade-canvas'
                        style={`width: ${cssW}; height: ${cssH};`}
                        onTouchStart={this.onCanvasTouchStart}
                        onTouchMove={this.onCanvasTouchMove}
                        onTouchEnd={this.onCanvasTouchEnd}
                    />

                    {screen === 'title' && (
                        <div class='arcade-overlay'>
                            <h3 class='mb-0'><Text id='arcade.pacman'>Pac-Man</Text></h3>
                            <div class='small'>
                                {isTouch
                                    ? <Text id='arcade.controlsPacmanTouch'>Swipe or use the arrows to move</Text>
                                    : <Text id='arcade.controlsPacmanKeyboard'>Arrows or WASD to move · P to pause</Text>}
                            </div>
                            <div class='d-flex flex-wrap justify-content-center gap-2'>
                                <button type='button' class='btn btn-warning' onClick={this.startGame}>
                                    <Text id='arcade.start'>Start game</Text>
                                </button>
                                {this.props.onBack && (
                                    <button type='button' class='btn btn-outline-light' onClick={this.props.onBack}>
                                        <Text id='arcade.backToArcade'>Back to arcade</Text>
                                    </button>
                                )}
                            </div>
                            <div class='small text-body-secondary'><Text id='arcade.insertCoin'>Press Space or tap Start</Text></div>
                        </div>
                    )}

                    {screen === 'game' && engineStatus === 'ready' && (
                        <div class='arcade-overlay' style='background: transparent;'>
                            <h4 class='mb-0 text-warning'><Text id='arcade.ready'>Ready!</Text></h4>
                        </div>
                    )}

                    {screen === 'game' && engineStatus === 'levelCleared' && (
                        <div class='arcade-overlay'>
                            <h4 class='mb-0'>
                                <Text id='arcade.levelCleared' fields={{ level }}>Level {level} cleared!</Text>
                            </h4>
                        </div>
                    )}

                    {screen === 'paused' && (
                        <div class='arcade-overlay'>
                            <div><Text id='arcade.paused'>Paused — press P or Resume to continue</Text></div>
                            <div class='d-flex flex-wrap justify-content-center gap-2'>
                                <button type='button' class='btn btn-warning btn-sm' onClick={this.resume}>
                                    <Text id='arcade.resume'>Resume</Text>
                                </button>
                                {this.props.onBack && (
                                    <button type='button' class='btn btn-outline-light btn-sm' onClick={this.props.onBack}>
                                        <Text id='arcade.backToArcade'>Back to arcade</Text>
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {screen === 'gameOver' && (
                        <div class='arcade-overlay'>
                            <h3 class='mb-0'><Text id='arcade.gameOver'>Game over</Text></h3>
                            <div><Text id='arcade.finalScore'>Final score</Text>: {score}</div>
                            {submitting && <div class='small'><Text id='arcade.savingScore'>Saving score…</Text></div>}
                            {!submitting && lastSubmit?.isNewBest && score > 0 && (
                                <div class='fw-bold'><Text id='arcade.newBest'>New personal best!</Text></div>
                            )}
                            <div class='d-flex flex-wrap justify-content-center gap-2'>
                                <button type='button' class='btn btn-warning' onClick={this.startGame}>
                                    <Text id='arcade.playAgain'>Play again</Text>
                                </button>
                                {this.props.onBack && (
                                    <button type='button' class='btn btn-outline-light' onClick={this.props.onBack}>
                                        <Text id='arcade.backToArcade'>Back to arcade</Text>
                                    </button>
                                )}
                                <button type='button' class='btn btn-outline-light' onClick={this.close}>
                                    <Text id='arcade.close'>Close</Text>
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {isTouch && inGame && this.renderDpad()}

                {inGame && !isTouch && (
                    <div class='d-flex gap-2 mt-3'>
                        <button type='button' class='btn btn-outline-light btn-sm' onClick={this.togglePause}>
                            {screen === 'paused'
                                ? <Text id='arcade.resume'>Resume</Text>
                                : <Text id='arcade.pause'>Pause</Text>}
                        </button>
                        <button type='button' class='btn btn-outline-light btn-sm' onClick={this.close}>
                            <Text id='arcade.close'>Close</Text>
                        </button>
                    </div>
                )}

                {(screen === 'title' || screen === 'gameOver') && (
                    <div class='w-100' style={`max-width: ${cssW};`}>
                        <ArcadeLeaderboard stores={this.props.stores} game={GAME_KEY} />
                    </div>
                )}
            </div>
        );
    }
}

export default Pacman;
